import { Box, Button, ButtonGroup, InlineStack } from '@shopify/polaris';
import PURCHASE_ORDER_STRINGS from '#client/pages/PurchaseOrders/constants/purchaseOrders.en.json';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
/**
 * PurchaseOrderActionButtons Component
 *
 * Renders action buttons for purchase order operations (Save Draft and Submit Document).
 * Handles button states, loading states, and validation logic for purchase order submission.
 *
 */
export default function PurchaseOrderActionButtons({
    handleSaveDraft,
    hasLineItems,
    handleSubmitDocument,
    submitDocumentLoading,
    submitSaveDraftLoading,
    onLocalStatusUpdate,
    purchaseOrder,
    displayStatus,
    hasOverreceived,
    scannedVariants,
    anyZeroReceived,
}) {
    const { poId } = useParams();
    const queryClient = useQueryClient();
    if (!purchaseOrder) return null;
    /**
     * Handles the Save Draft button click
     * Updates local status and triggers the save draft handler
     */

    const handleSaveDraftClick = async () => {
        if (onLocalStatusUpdate) onLocalStatusUpdate(true);

        await handleSaveDraft();

        // After the save draft button has been clicked, invalidate the activity log and cause it to refetch so it grabs non-stale data and updates the component
        queryClient.invalidateQueries({ queryKey: ['activity-logs'] });
        // Also, invalidate the purchase order detail to ensure it reflects any changes
        queryClient.invalidateQueries({ queryKey: ['PurchaseOrderDetail', poId] });
    };

    /**
     * Handles the Submit Document button click
     * Updates local status, triggers submit callback, and submits the document
     */
    const handleSubmitDocumentClick = async () => {
        if (onLocalStatusUpdate) onLocalStatusUpdate(true);

        await handleSubmitDocument();

        // After the submit button has been clicked, invalidate the activity log and cause it to refetch so it grabs non-stale data and updates the component
        queryClient.invalidateQueries({ queryKey: ['activity-logs'] });
        // Also, invalidate the purchase order detail to ensure it reflects any changes
        queryClient.invalidateQueries({ queryKey: ['PurchaseOrderDetail', poId] });
    };

    /**
     * Determines if the Submit button should be disabled
     *
     * The submit button is disabled when:  No line items exist OR Items have been over-received OR Any line item doesn't have a product on the purchase order
     */
    const isSubmitButtonDisabled = () => {
        if (!Array.isArray(scannedVariants) || !hasLineItems || hasOverreceived || anyZeroReceived) return true;
        for (let i = 0; i < scannedVariants.length; i++) {
            if (!scannedVariants[i].productOnPurchaseOrder) {
                return true; // Found an item without productOnPurchaseOrder, so disable button
            }
        }
        return false; // All items have productOnPurchaseOrder, so enable button
    };

    return (
        <>
            {displayStatus !== 'Completed' && (
                <InlineStack align="end" gap={{ xs: 200 }}>
                    <Box>
                        <ButtonGroup>
                            {/* Save Draft Button */}
                            <Button variant="primary" onClick={handleSaveDraftClick} loading={submitSaveDraftLoading} disabled={!hasLineItems}>
                                {PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_ACTION_BUTTONS.SAVE_DRAFT}
                            </Button>

                            {/* Submit Document Button */}
                            <Button variant="primary" onClick={handleSubmitDocumentClick} loading={submitDocumentLoading} disabled={isSubmitButtonDisabled()}>
                                {PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_ACTION_BUTTONS.SUBMIT_DOCUMENT}
                            </Button>
                        </ButtonGroup>
                    </Box>
                </InlineStack>
            )}
        </>
    );
}
